import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import {
  ApiCisternResponseDto,
  ApiDeviceGroupResponseDto,
  DeviceTargetType,
  DeviceType,
} from '@birco/rainmesh-api';
import { Action, ActionsSubject, Store } from '@ngrx/store';
import { AsyncPipe, Location } from '@angular/common';
import {
  selectDeviceById,
  selectIsDevicesLoading,
  selectIsDeviceUsableInRule,
} from '../../state/device.selectors';
import { RuleViewModel } from '../../../rules/models/rule-view-model.interface';
import { RuleActions } from '../../../rules/state/rule.actions';
import { TextWithIconComponent } from '../../../../shared/components/text-with-icon/text-with-icon.component';
import { RelativeDatePipe } from '../../../../shared/pipes/relative-date.pipe';
import { IconsPipe } from '../../../../shared/pipes/icons.pipe';
import { selectRulesByDeviceIdAsViewModel } from '../../../rules/state/rule.selectors';
import { MapWithLocationComponent } from '../../../map/components/map-with-location/map-with-location.component';
import { MapDevice } from '../../../map/models/map-device.interface';
import { DeviceActions } from '../../state/device.actions';
import { DialogUpdateDeviceComponent } from '../../components/dialog-update-device/dialog-update-device.component';
import { selectIsSystemRunning } from '../../../dashboard/state/kill-switch.selectors';
import { HistoryComponent } from '../../components/history/history.component';
import { NotFoundComponent } from '../../../../shared/components/error/not-found/not-found.component';
import { HistoryService } from '../../../../core/services/history.service';
import { DeviceResponseDto } from '../../models/device-response.interface';
import {
  isDeviceActive,
  isDevicePending,
} from '../../utils/device-value.utils';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import {
  ContextMenuComponent,
  ContextMenuData,
} from '../../../../shared/components/context-menu/context-menu.component';
import { CisternActions } from '../../../cisterns/state/cistern.actions';
import {
  selectCisternById,
  selectCisternBySensorId,
} from '../../../cisterns/state/cistern.selectors';
import { DeviceGroupActions } from '../../state/device-groups/device-group.actions';
import { selectDeviceGroupsByDevice } from '../../state/device-groups/device-group.selectors';
import { AreaItemCardComponent } from '../../components/area-item-card/area-item-card.component';
import { DeviceActionTriggerComponent } from '../../components/device-action-trigger/device-action-trigger.component';
import { DeviceItemCardComponent } from '../../components/device-item-card/device-item-card.component';
import { CisternItemCardComponent } from '../../../cisterns/components/cistern-item-card/cistern-item-card.component';
import { DeviceInfoTableComponent } from '../../components/device-info-table/device-info-table.component';
import { DeviceRulesCardComponent } from '../../components/device-rules-card/device-rules-card.component';

@Component({
  selector: 'app-page-device-detail',
  imports: [
    CardComponent,
    IconComponent,
    TranslatePipe,
    HistoryComponent,
    AsyncPipe,
    TextWithIconComponent,
    RelativeDatePipe,
    IconsPipe,
    MapWithLocationComponent,
    NotFoundComponent,
    TagComponent,
    ContextMenuComponent,
    AreaItemCardComponent,
    DeviceActionTriggerComponent,
    DeviceItemCardComponent,
    CisternItemCardComponent,
    DeviceInfoTableComponent,
    DeviceRulesCardComponent,
  ],
  templateUrl: './page-device-detail.component.html',
  styleUrl: './page-device-detail.component.scss',
})
export class PageDeviceDetailComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private historyService = inject(HistoryService);
  private translateService = inject(TranslateService);
  private actions$ = inject(ActionsSubject);
  private snackBar = inject(MatSnackBar);

  loading$ = this.store.select(selectIsDevicesLoading);

  device$!: Observable<DeviceResponseDto | null>;
  deviceGroups$!: Observable<ApiDeviceGroupResponseDto[]>;
  targetCistern$!: Observable<ApiCisternResponseDto | null>;
  connectedCistern$!: Observable<ApiCisternResponseDto | undefined>;
  isUsableInRule$?: Observable<boolean>;
  ruleList$?: Observable<RuleViewModel[]>;
  history$ = this.historyService.getHistoryData$();
  killSwitch$ = this.store.select(selectIsSystemRunning);
  async getDeviceId() {
    return await firstValueFrom(
      this.device$.pipe(
        filter((d) => !!d),
        map((d) => d?.id),
      ),
    );
  }

  ngOnInit() {
    this.store.dispatch(CisternActions.public.loadAllCisternsByFilters({}));
    this.store.dispatch(
      DeviceActions.public.loadAllDevicesByFilters({
        filter: {},
      }),
    );

    const deviceId$ = this.route.paramMap.pipe(
      map((params) => params.get('id')),
      filter((id): id is string => !!id),
      distinctUntilChanged(),
    );

    deviceId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.historyService.reset();
      this.store.dispatch(
        DeviceActions.public.loadDeviceById({
          id,
        }),
      );
      this.store.dispatch(
        RuleActions.public.loadAllRulesByFilters({
          filter: {
            deviceId: id,
          },
        }),
      );
    });

    this.device$ = deviceId$.pipe(
      switchMap((id) => this.store.select(selectDeviceById(id))),
      catchError((_) => EMPTY),
    );

    this.deviceGroups$ = deviceId$.pipe(
      switchMap((id) => this.store.select(selectDeviceGroupsByDevice(id))),
      catchError((_) => EMPTY),
    );

    deviceId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.store.dispatch(
        DeviceGroupActions.public.loadAllDeviceGroupsByFilters({}),
      );
    });

    this.connectedCistern$ = this.device$.pipe(
      switchMap((device) =>
        device ? this.store.select(selectCisternBySensorId(device.id)) : EMPTY,
      ),
      catchError((_) => EMPTY),
    );

    this.route.queryParamMap
      .pipe(
        map((queryParams) => queryParams.get('edit') === 'true'),
        filter((shouldEdit) => shouldEdit),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { edit: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        void this.onEdit();
      });

    this.targetCistern$ = this.device$.pipe(
      switchMap((device) => {
        const targetCisternId = device?.targetCisternId;
        if (!targetCisternId) {
          return of(null);
        }

        return this.store.select(selectCisternById(targetCisternId));
      }),
      catchError((_) => EMPTY),
    );

    this.isUsableInRule$ = deviceId$.pipe(
      switchMap((id) => this.store.select(selectIsDeviceUsableInRule(id))),
      catchError((_) => EMPTY),
    );

    this.ruleList$ = deviceId$.pipe(
      switchMap((id) =>
        this.store.select(selectRulesByDeviceIdAsViewModel(id)),
      ),
      catchError((_) => EMPTY),
    );

    deviceId$
      .pipe(
        switchMap((id) =>
          this.store
            .select(selectIsDeviceUsableInRule(id))
            .pipe(map((isUsableInRule) => ({ id, isUsableInRule }))),
        ),
        distinctUntilChanged(
          (a, b) => a.id === b.id && a.isUsableInRule === b.isUsableInRule,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ id, isUsableInRule }) => {
        if (isUsableInRule) {
          this.historyService.loadInitial(id);
          return;
        }

        this.historyService.reset();
      });
  }

  async onTriggerDeviceAction(value: boolean) {
    const device = await firstValueFrom(
      this.device$.pipe(
        filter(
          (currentDevice): currentDevice is DeviceResponseDto =>
            !!currentDevice,
        ),
        take(1),
      ),
    );
    if (device.isOutOfService) {
      return;
    }

    this.store.dispatch(
      DeviceActions.public.triggerDeviceAction({
        id: device.id,
        value: value,
      }),
    );
  }

  get isActive$() {
    return this.device$.pipe(map((d) => !!d?.isActuator && isDeviceActive(d)));
  }

  protected readonly isDevicePending = isDevicePending;

  onBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/', RouteConstants.routeDevice]);
  }

  async onDelete(): Promise<void> {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('devices.delete.title'),
      body: this.translateService.instant('devices.delete.body'),
      confirmLabel: this.translateService.instant('default.delete'),
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    const id = await this.getDeviceId();

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((result): result is true => result === true),
      )
      .subscribe((result) => {
        this.store.dispatch(DeviceActions.public.deleteDeviceById({ id: id }));
      });
  }

  async onEdit(): Promise<void> {
    const device = await firstValueFrom(
      this.device$.pipe(
        filter(
          (currentDevice): currentDevice is DeviceResponseDto =>
            !!currentDevice,
        ),
        take(1),
      ),
    );

    const dialogRef = this.dialog.open(DialogUpdateDeviceComponent, {
      width: '48rem',
      data: {
        device: {
          ...device,
          sourcePumpId: device.sourcePump?.id ?? null,
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        this.actions$
          .pipe(
            filter(
              (action: Action) =>
                (this.isUpdateDeviceSuccessAction(action) &&
                  action.responseDto.id === result.id) ||
                (this.isUpdateDeviceFailureAction(action) &&
                  action.id === result.id),
            ),
            take(1),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((action: Action) => {
            if (this.isUpdateDeviceFailureAction(action)) {
              this.snackBar.open(
                this.translateService.instant('devices.edit.saveError'),
                this.translateService.instant('default.ok'),
                { duration: 7000 },
              );
            }
          });

        this.store.dispatch(DeviceActions.public.updateDevice(result));
      });
  }

  private isUpdateDeviceSuccessAction(
    action: Action,
  ): action is ReturnType<typeof DeviceActions.private.updateDeviceSuccess> {
    return action.type === DeviceActions.private.updateDeviceSuccess.type;
  }

  private isUpdateDeviceFailureAction(
    action: Action,
  ): action is ReturnType<typeof DeviceActions.private.updateDeviceFailure> {
    return action.type === DeviceActions.private.updateDeviceFailure.type;
  }

  toggleOutOfService(
    device: Pick<DeviceResponseDto, 'id' | 'isOutOfService'>,
  ): void {
    this.store.dispatch(
      DeviceActions.public.updateDevice({
        id: device.id,
        isOutOfService: !device.isOutOfService,
      }),
    );
  }

  getContextMenu(device: DeviceResponseDto): ContextMenuData[] {
    return [
      {
        labelKey: device.isFavorite
          ? 'devices.favoriteRemove'
          : 'devices.favoriteAdd',
        action: () => this.onToggleFavorite(device),
      },
      {
        labelKey: device.isOutOfService
          ? 'devices.detail.outOfService.remove'
          : 'devices.detail.outOfService.set',
        action: () => this.toggleOutOfService(device),
      },
    ];
  }

  onToggleFavorite(device: Pick<DeviceResponseDto, 'id' | 'isFavorite'>): void {
    this.store.dispatch(
      DeviceActions.public.updateDevice({
        id: device.id,
        isFavorite: !device.isFavorite,
      }),
    );
  }

  shouldShowValveCisternDetails(device: DeviceResponseDto): boolean {
    return (
      device.type === DeviceType.VALVE &&
      device.deviceTargetType === DeviceTargetType.CISTERN
    );
  }

  shouldShowValveTargetType(device: DeviceResponseDto): boolean {
    return device.type === DeviceType.VALVE;
  }

  getTargetTypeLabelKey(deviceTargetType?: DeviceTargetType | null): string {
    if (!deviceTargetType) {
      return 'devices.edit.targetTypeOptions.none';
    }

    return `devices.edit.targetTypeOptions.${deviceTargetType.toLowerCase()}`;
  }

  getMapDevice(device: DeviceResponseDto): MapDevice {
    return {
      id: device.id,
      name: device.name,
      latitude: device.location?.lat,
      longitude: device.location?.lon,
      type: device.type,
      isFavorite: device.isFavorite,
      isOutOfService: device.isOutOfService,
    };
  }

  getError() {
    return {
      buttonLabelKey: 'devices.detail.error.goTo',
      buttonLink: [RouteConstants.routeDevice],
    };
  }

  async onLoadMoreHistory(): Promise<void> {
    const id = await this.getDeviceId();
    this.historyService.loadMore(id);
  }

  async onCreateRule(): Promise<void> {
    const device = await firstValueFrom(
      this.device$.pipe(
        filter(
          (currentDevice): currentDevice is DeviceResponseDto =>
            !!currentDevice,
        ),
        take(1),
      ),
    );

    if (device.isOutOfService) {
      return;
    }

    await this.router.navigate([
      '/',
      RouteConstants.routeRule,
      RouteConstants.routeAdd,
    ]);
  }

  protected readonly RouteConstants = RouteConstants;
}
